import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Phone, MessageSquare, Mail, Bot, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Agents = () => {
  const { getAuthHeader } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'voice',
    prompt: '',
    config: {}
  });

  const agentTemplates = {
    voice: `You are Jason, a professional real estate assistant from Exit Realty Specialists in the Saint John, NB area. Your role is to qualify leads interested in buying or selling property.

Your approach:
1. Greet warmly and confirm their interest
2. Ask about their property needs (buying/selling/renting)
3. Understand their location preference in Greater Saint John area
4. Discuss budget and timeline
5. Qualify their readiness to proceed
6. Book a consultation with Justin Saulnier (REALTOR®)

Contact Info:
- Email: justin@exitsj.ca
- Office: +1 506-642-3948
- Mobile: +1 506-647-4510
- Website: movewithjustin.ca

Be conversational, empathetic, and professional.`,
    sms: `You are a professional real estate SMS assistant. Keep messages concise, friendly, and action-oriented.

For lead qualification:
1. Introduce yourself briefly
2. Ask one clear question at a time
3. Confirm their interest in Saint John, NB properties
4. Offer to schedule a call or meeting
5. Provide contact details if requested

Always be respectful of SMS character limits.`,
    email: `You are a professional real estate email assistant for Exit Realty Specialists.

For lead qualification emails:
1. Professional greeting with personalization
2. Brief introduction to Exit Realty Specialists
3. Acknowledge their interest in Saint John, NB real estate
4. Present 2-3 qualifying questions
5. Include clear call-to-action
6. Provide full contact details and signature

Maintain professional yet warm tone. Format emails with proper structure.`
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`, {
        headers: getAuthHeader()
      });
      setAgents(response.data);
    } catch (error) {
      toast.error('Failed to fetch agents');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/agents`, formData, {
        headers: getAuthHeader()
      });
      toast.success('Agent created successfully');
      setDialogOpen(false);
      setFormData({ name: '', type: 'voice', prompt: '', config: {} });
      fetchAgents();
    } catch (error) {
      toast.error('Failed to create agent');
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    try {
      await axios.delete(`${API}/agents/${agentId}`, {
        headers: getAuthHeader()
      });
      toast.success('Agent deleted successfully');
      fetchAgents();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  const handleTypeChange = (type) => {
    setFormData({
      ...formData,
      type,
      prompt: agentTemplates[type]
    });
  };

  const getAgentIcon = (type) => {
    switch (type) {
      case 'voice':
        return Phone;
      case 'sms':
        return MessageSquare;
      case 'email':
        return Mail;
      default:
        return Bot;
    }
  };

  const getAgentColor = (type) => {
    switch (type) {
      case 'voice':
        return 'from-blue-400 to-blue-600';
      case 'sms':
        return 'from-green-400 to-green-600';
      case 'email':
        return 'from-purple-400 to-purple-600';
      default:
        return 'from-slate-400 to-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-600">Loading agents...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">AI Agents</h1>
          <p className="text-slate-600">Configure your voice, SMS, and email agents</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-agent-button"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Agent
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create AI Agent</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAgent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Agent Name *</label>
                <Input
                  data-testid="agent-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Jason - Voice Qualifier"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Agent Type *</label>
                <Select
                  value={formData.type}
                  onValueChange={handleTypeChange}
                >
                  <SelectTrigger data-testid="agent-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="voice">Voice Agent (Phone Calls)</SelectItem>
                    <SelectItem value="sms">SMS Agent (Text Messages)</SelectItem>
                    <SelectItem value="email">Email Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Agent Prompt *</label>
                <Textarea
                  data-testid="agent-prompt-input"
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  rows={12}
                  className="font-mono text-sm"
                  required
                />
              </div>
              <Button
                data-testid="submit-agent-button"
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600"
              >
                Create Agent
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pre-built Templates */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-0 shadow-lg">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Quick Start Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              setFormData({
                name: 'Voice Qualifier - Jason',
                type: 'voice',
                prompt: agentTemplates.voice,
                config: {}
              });
              setDialogOpen(true);
            }}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-all text-left"
          >
            <Phone className="w-8 h-8 text-blue-600 mb-2" />
            <div className="font-semibold text-slate-800">Voice Agent</div>
            <div className="text-sm text-slate-600">AI-powered phone calls</div>
          </button>
          <button
            onClick={() => {
              setFormData({
                name: 'SMS Qualifier',
                type: 'sms',
                prompt: agentTemplates.sms,
                config: {}
              });
              setDialogOpen(true);
            }}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-all text-left"
          >
            <MessageSquare className="w-8 h-8 text-green-600 mb-2" />
            <div className="font-semibold text-slate-800">SMS Agent</div>
            <div className="text-sm text-slate-600">Automated text responses</div>
          </button>
          <button
            onClick={() => {
              setFormData({
                name: 'Email Qualifier',
                type: 'email',
                prompt: agentTemplates.email,
                config: {}
              });
              setDialogOpen(true);
            }}
            className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-all text-left"
          >
            <Mail className="w-8 h-8 text-purple-600 mb-2" />
            <div className="font-semibold text-slate-800">Email Agent</div>
            <div className="text-sm text-slate-600">Professional email responses</div>
          </button>
        </div>
      </Card>

      {/* Agents Grid */}
      {agents.length === 0 ? (
        <Card className="p-12 text-center">
          <Bot className="w-16 h-16 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 mb-4">No agents configured yet. Create your first AI agent!</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-gradient-to-r from-blue-500 to-purple-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Agent
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agents.map((agent) => {
            const Icon = getAgentIcon(agent.type);
            return (
              <Card
                key={agent.id}
                data-testid={`agent-card-${agent.id}`}
                className="p-6 card-hover bg-white border-0 shadow-lg"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getAgentColor(
                        agent.type
                      )} flex items-center justify-center shadow-lg`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">{agent.name}</h3>
                      <span className="text-sm text-slate-600 capitalize">
                        {agent.type} Agent
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-slate-600 line-clamp-3">
                    {agent.prompt.substring(0, 150)}...
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    data-testid={`test-agent-${agent.id}`}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    Test Agent
                  </Button>
                  <Button
                    data-testid={`delete-agent-${agent.id}`}
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteAgent(agent.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Agents;